import type { CategoryTotal } from '@apka/core'
import { useEffect, useState } from 'react'
import type { Kategoria } from '../dane/typy.js'
import { zl } from './format.js'
import './wykresy.css'

interface Props {
  udzialy: readonly CategoryTotal[]
  kategorie: ReadonlyMap<string, Kategoria>
  maks?: number
}

const PROMIEN = 54
const OBWOD = 2 * Math.PI * PROMIEN
const KOLOR_BEZ_KATEGORII = '#6B6B75'

/**
 * Pierścień rysowany przesuwanym `stroke-dasharray` — bez biblioteki i bez
 * kosztu jej wczytania. Wartości są już policzone przez rdzeń, tu zostaje
 * sama geometria.
 */
export function WykresKategorii({ udzialy, kategorie, maks = 6 }: Props) {
  // Łuki narastają dopiero po pierwszej klatce — gotowy pierścień nie mówi
  // nic o proporcjach, a narastający pokazuje, co jest większe od czego.
  const [narysowane, ustawNarysowane] = useState(false)
  useEffect(() => {
    const klatka = requestAnimationFrame(() => ustawNarysowane(true))
    return () => cancelAnimationFrame(klatka)
  }, [])

  const suma = udzialy.reduce((razem, wpis) => razem + wpis.total, 0)
  if (suma <= 0) {
    return <p className="wykres__pusto">Brak wydatków do pokazania.</p>
  }

  const czolowe = udzialy.slice(0, maks)
  const reszta = udzialy.slice(maks).reduce((razem, wpis) => razem + wpis.total, 0)
  const pozycje = reszta > 0 ? [...czolowe, { categoryId: 'reszta', total: reszta }] : czolowe

  let przesuniecie = 0
  const luki = pozycje.map((wpis) => {
    const dlugosc = (wpis.total / suma) * OBWOD
    const luk = {
      wpis,
      dlugosc,
      przesuniecie,
      kolor:
        wpis.categoryId === 'reszta' || wpis.categoryId === null
          ? KOLOR_BEZ_KATEGORII
          : (kategorie.get(wpis.categoryId)?.color ?? KOLOR_BEZ_KATEGORII),
      nazwa:
        wpis.categoryId === 'reszta'
          ? 'Pozostałe'
          : wpis.categoryId === null
            ? 'Bez kategorii'
            : (kategorie.get(wpis.categoryId)?.name ?? 'Bez kategorii'),
    }
    przesuniecie += dlugosc
    return luk
  })

  const opis = luki
    .map((luk) => `${luk.nazwa}: ${zl(luk.wpis.total)}`)
    .join(', ')

  return (
    <div className="wykres-kategorii">
      <svg
        viewBox="0 0 140 140"
        className="wykres-kategorii__pierscien"
        role="img"
        aria-label={`Podział wydatków. ${opis}`}
      >
        <circle cx="70" cy="70" r={PROMIEN} className="wykres-kategorii__tlo" />
        {luki.map((luk, indeks) => (
          <circle
            key={luk.wpis.categoryId ?? 'brak'}
            cx="70"
            cy="70"
            r={PROMIEN}
            stroke={luk.kolor}
            strokeDasharray={
              narysowane ? `${Math.max(luk.dlugosc - 2, 0)} ${OBWOD}` : `0 ${OBWOD}`
            }
            strokeDashoffset={-luk.przesuniecie}
            style={{ transitionDelay: `${indeks * 60}ms` }}
            className="wykres-kategorii__luk"
          />
        ))}
        <text x="70" y="66" className="wykres-kategorii__suma">
          {zl(suma)}
        </text>
        <text x="70" y="82" className="wykres-kategorii__podpis">
          razem
        </text>
      </svg>

      <ul className="wykres-kategorii__legenda">
        {luki.map((luk) => (
          <li key={luk.wpis.categoryId ?? 'brak'}>
            <span className="kropka-kategorii" style={{ background: luk.kolor }} aria-hidden="true" />
            <span className="wykres-kategorii__nazwa">{luk.nazwa}</span>
            <span className="wykres-kategorii__kwota">{zl(luk.wpis.total)}</span>
            <span className="wykres-kategorii__procent">
              {Math.round((luk.wpis.total / suma) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
